
import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import PageHero from '@/components/ui/PageHero';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import FAQSection from '@/components/ui/FAQSection';
import PricingSection from '@/components/ui/PricingSection';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { Camera, Building, Home, Hospital, Hotel, Store, Video, FileDigit, PhoneCall, Landmark, PenTool, Link as LinkIcon, Computer, Smartphone, Globe, User, Target, Check, CreditCard } from 'lucide-react';

const VirtualTours = () => {
  const faqs = [
    {
      question: "What are virtual tours and how do they work?",
      answer: "Virtual tours are 360° immersive interactive digital walk-throughs of physical spaces. Using specialized 360° cameras, we capture your space from multiple angles and then stitch these images together to create a cohesive, navigable experience. Unlike standard photography, virtual tours allow visitors to explore spaces as if they were physically present, moving from room to room and examining details from any angle."
    },
    {
      question: "How are your virtual tours different from Matterport?",
      answer: "As a Matterport alternative, we create completely custom virtual tours using professional 360° cameras and advanced software. Our approach gives us greater flexibility to add interactive elements like embedded videos, audio narration, custom links, information hotspots, and even live video call capabilities. Plus, our hosting solutions are much more cost-effective than Matterport's subscription model."
    },
    {
      question: "What industries can benefit from virtual tours?",
      answer: "Virtual tours are versatile and beneficial for numerous industries including real estate (both existing and under construction properties), retail stores, hospitality venues, restaurants, medical facilities, dental offices, event spaces, wedding venues, churches, educational institutions, shared workspaces, and more in Baton Rouge, Louisiana and beyond. Any physical space that you want to showcase can be transformed into an engaging virtual experience."
    },
    {
      question: "Do you offer hosting for the virtual tours?",
      answer: "Yes, we offer several hosting options. We can host the tour on our secure servers for a modest monthly fee that's significantly more affordable than competitors like Matterport. Alternatively, we can provide you with the complete tour files for self-hosting on your own servers or for storage on physical media like hard drives or USB drives. We're flexible and will work with your specific needs."
    },
    {
      question: "Can virtual tours be embedded on our website?",
      answer: "Absolutely! Our virtual tours are designed to be easily embedded on your website, creating an engaging interactive feature that allows visitors to explore your space directly from your site. This significantly increases visitor engagement and time spent on your website, which can improve search engine rankings and conversion rates."
    },
    {
      question: "Can you create virtual tours for buildings that aren't completed yet?",
      answer: "Yes! We can create virtual tours for spaces still under construction in Baton Rouge, Louisiana, including buildings in the framing or rough-in stage where electrical, HVAC, and structural elements are visible. We can also create virtual tours from 3D renders of planned spaces, giving potential buyers or stakeholders an immersive preview of what the completed project will look like."
    }
  ];

  const pricingPlans = [
    {
      name: "Basic Virtual Tour",
      price: "$300",
      description: "Perfect for small spaces and simple showcases",
      features: [
        { text: "Up to 15 panoramic views", included: true },
        { text: "Basic navigation and mobile-friendly interface", included: true },
        { text: "Perfect for small homes or single offices", included: true },
        { text: "Embed on your website or host yourself", included: true },
        { text: "Great for rental properties", included: true },
        { text: "Ideal for retail shops under 1,500 sq ft", included: true },
        { text: "Shareable direct link for social media", included: true },
        { text: "Showcases small event venues", included: true },
        { text: "Excellent for apartments or condos", included: true }
      ],
      popular: false,
      ctaText: "Get Started",
      ctaLink: "mailto:hello@astrem.co?subject=Virtual Tour Inquiry for "
    },
    {
      name: "Premium Virtual Tour",
      price: "$800",
      description: "Ideal for medium to large spaces with detailed features",
      features: [
        { text: "Up to 40 panoramic views", included: true },
        { text: "Perfect for larger homes or offices", included: true },
        { text: "Great for multi-floor restaurants", included: true },
        { text: "Ideal for medical facilities", included: true },
        { text: "Enhanced navigation capabilities", included: true },
        { text: "Excellent for hotels or B&Bs", included: true },
        { text: "Showcases retail spaces beautifully", included: true },
        { text: "Ideal for educational institutions", included: true },
        { text: "Perfect for real estate listings", included: true }
      ],
      popular: true,
      ctaText: "Most Popular",
      ctaLink: "mailto:hello@astrem.co?subject=Virtual Tour Inquiry for "
    },
    {
      name: "Enterprise Solution",
      price: "$2,000",
      description: "Comprehensive solution for complex or multi-location spaces",
      features: [
        { text: "Up to 100 panoramic views", included: true },
        { text: "Perfect for large commercial properties", included: true },
        { text: "Ideal for college campuses", included: true },
        { text: "Great for multi-building facilities", included: true },
        { text: "Excellent for large shopping centers", included: true },
        { text: "Complete interactive experience", included: true },
        { text: "Perfect for hospitals or large clinics", included: true },
        { text: "Ideal for extensive manufacturing spaces", included: true },
        { text: "Showcases large event venues or conference centers", included: true }
      ],
      popular: false,
      ctaText: "Contact Us",
      ctaLink: "mailto:hello@astrem.co?subject=Virtual Tour Inquiry for "
    }
  ];

  const zillowPricing = [
    {
      sqft: "800 to 1,500 sqft",
      price: "$150"
    },
    {
      sqft: "1,500 to 3,000 sqft",
      price: "$225"
    },
    {
      sqft: "3,000 to 4,000 sqft",
      price: "$350"
    }
  ];

  const industries = [
    {
      icon: <Home />,
      title: "Real Estate",
      description: "Showcase properties before they're even built or give potential buyers a comprehensive view of existing homes, allowing them to explore at their own pace."
    },
    {
      icon: <Building />,
      title: "Construction",
      description: "Document construction progress at the framing or rough-in stage, allowing clients to see electrical, HVAC, and structural elements before walls are closed."
    },
    {
      icon: <Hotel />,
      title: "Hospitality",
      description: "Let potential guests explore your hotel rooms, amenities, and public spaces, helping them feel comfortable with their booking decision."
    },
    {
      icon: <Store />,
      title: "Retail",
      description: "Give customers a virtual shopping experience and showcase your store layout and product displays to entice in-store visits."
    },
    {
      icon: <Hospital />,
      title: "Healthcare",
      description: "Help patients familiarize themselves with your medical or dental facilities before their visit, reducing anxiety and improving the patient experience."
    },
    {
      icon: <Landmark />,
      title: "Event Venues",
      description: "Allow event planners and couples to tour your wedding venue or event space remotely, helping them visualize their special occasion."
    }
  ];

  const features = [
    {
      icon: <PenTool />,
      title: "Fully Customizable",
      description: "As a Matterport alternative, our tours can be completely customized with your branding, interactive elements, and specific features."
    },
    {
      icon: <Video />,
      title: "Multimedia Integration",
      description: "Embed videos, audio narration, photo galleries, and other rich media directly within your virtual tour for a comprehensive presentation."
    },
    {
      icon: <LinkIcon />,
      title: "Interactive Hotspots",
      description: "Add informational hotspots, website links, and call-to-actions throughout your tour to engage visitors and provide additional details."
    },
    {
      icon: <PhoneCall />,
      title: "Video Call Capability",
      description: "Conduct live guided tours remotely with embedded video call functionality, allowing real-time interaction with potential clients or customers."
    },
    {
      icon: <Computer />,
      title: "Kiosk Mode",
      description: "Use your virtual tour on touchscreen displays in lobbies, reception areas, or at trade shows to create engaging interactive experiences."
    },
    {
      icon: <FileDigit />,
      title: "Flexible Hosting Options",
      description: "Choose from our affordable hosting plans or receive the complete tour files to host on your own servers or store on physical media."
    }
  ];

  const addOns = [
    "Video integration", 
    "Highlights tooltips", 
    "Audio narration", 
    "Voiceovers", 
    "Web links", 
    "Text information hotspots",
    "Interactive hotspots with images",
    "Custom branding options",
    "Embedded image galleries",
    "USB with copy of virtual tour",
    "Live video call capability",
    "Advanced interactive elements",
    "Custom hosting solutions"
  ];

  const paymentMethods = [
    "Credit/Debit Card",
    "Klarna (Buy Now, Pay Later)",
    "Affirm (Buy Now, Pay Later)"
  ];

  return (
    <PageLayout 
      title="Virtual Tours | Astrem" 
      description="Custom 360° immersive virtual tours for businesses, real estate, and more. Better than Matterport with affordable pricing."
      ogImage="public/lovable-uploads/56772ea4-469d-4a78-a80f-8b17ca933085.png"
    >
      <PageHero
        title="Custom 360° Virtual Tours"
        subtitle="Immersive Interactive Experiences in Baton Rouge, Louisiana"
        description="Showcase your spaces with fully interactive, professionally created virtual tours that engage visitors and bring your physical location to life online."
        badge="Virtual Tours"
        backgroundImage="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Person_setting_up_camera_tripod.jpg"
        ctaText="Request a Demo"
        ctaLink="/contact"
      />

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Matterport Alternative
              </span>
              <h2 className="text-3xl md:text-4xl font-medium mb-4">Custom Virtual Tours, Your Way</h2>
              <p className="text-lg text-astrem-gray-600 max-w-3xl mx-auto">
                We offer a Matterport alternative with more features, better flexibility, and at a lower cost. Our virtual tours in Baton Rouge, Louisiana provide a premium experience for your clients.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <Card className="h-full border border-astrem-gray-200">
                  <CardContent className="p-6">
                    <div className="text-astrem-black mb-4 text-3xl">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-medium mb-2">{feature.title}</h3>
                    <p className="text-astrem-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <span className="inline-block px-3 py-1 bg-white text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Tour Example
              </span>
              <h2 className="text-3xl md:text-4xl font-medium mb-4">Experience Our Virtual Tours</h2>
              <p className="text-lg text-astrem-gray-600 max-w-3xl mx-auto">
                Explore this sample virtual tour to see the quality and features of our custom tours. Navigate through the space as if you were actually there.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <div className="max-w-5xl mx-auto rounded-lg overflow-hidden shadow-lg">
              <div className="aspect-w-16 aspect-h-9">
                <iframe 
                  frameBorder="0" 
                  src="https://astrem.d.pr/09hQDV" 
                  name="The Hill St. Francisville" 
                  allowFullScreen={true}
                  allow="fullscreen; accelerometer; gyroscope; magnetometer; vr; xr; xr-spatial-tracking; autoplay; camera; microphone" 
                  id="icepio-2"
                  className="w-full h-[600px]"
                ></iframe>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={200}>
            <div className="text-center mt-8">
              <p className="text-astrem-gray-600 mb-4">
                This is an example of our premium virtual tour created for a property in St. Francisville, near Baton Rouge, Louisiana.
              </p>
              <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full px-6">
                <a href="mailto:hello@astrem.co?subject=Virtual Tour Inquiry for">Request Your Custom Tour</a>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Industry Solutions
              </span>
              <h2 className="text-3xl md:text-4xl font-medium mb-4">Virtual Tours for Every Industry</h2>
              <p className="text-lg text-astrem-gray-600 max-w-3xl mx-auto">
                From real estate and construction to hospitality and healthcare, our virtual tours in Baton Rouge, Louisiana can be tailored to meet the specific needs of your industry.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <Card className="h-full border border-astrem-gray-200 bg-white">
                  <CardContent className="p-6">
                    <div className="text-astrem-black mb-4 text-3xl">
                      {industry.icon}
                    </div>
                    <h3 className="text-xl font-medium mb-2">{industry.title}</h3>
                    <p className="text-astrem-gray-600">{industry.description}</p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={400}>
            <div className="mt-12 text-center">
              <Button asChild size="lg" className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full px-8">
                <a href="mailto:hello@astrem.co?subject=Virtual Tour Inquiry for Baton Rouge">
                  Discuss Your Industry Needs
                </a>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Our Process
              </span>
              <h2 className="text-3xl md:text-4xl font-medium mb-4">How Our Virtual Tours Work</h2>
              <p className="text-lg text-astrem-gray-600 max-w-3xl mx-auto">
                Creating a custom virtual tour in Baton Rouge, Louisiana is a straightforward process that delivers exceptional results.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ScrollReveal>
              <div className="text-center p-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-astrem-gray-100 text-astrem-black mb-4">
                  <Camera size={32} />
                </div>
                <h3 className="text-xl font-medium mb-2">1. Capture</h3>
                <p className="text-astrem-gray-600">
                  We use professional 360° cameras to capture high-resolution images of your space from strategic viewpoints.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <div className="text-center p-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-astrem-gray-100 text-astrem-black mb-4">
                  <Target size={32} />
                </div>
                <h3 className="text-xl font-medium mb-2">2. Create</h3>
                <p className="text-astrem-gray-600">
                  Our team stitches the images together, creates navigation, and adds custom interactive elements based on your needs.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="text-center p-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-astrem-gray-100 text-astrem-black mb-4">
                  <Globe size={32} />
                </div>
                <h3 className="text-xl font-medium mb-2">3. Deliver</h3>
                <p className="text-astrem-gray-600">
                  We provide you with an engaging, interactive virtual tour that can be embedded on your website or hosted with our affordable solutions.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Customizations
              </span>
              <h2 className="text-3xl md:text-4xl font-medium mb-4">Additional Customization Options</h2>
              <p className="text-lg text-astrem-gray-600 max-w-3xl mx-auto">
                Enhance your virtual tour with these additional customization options. We can create an entirely custom tour for your specific needs in Baton Rouge, Louisiana.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-2 gap-x-6">
            {addOns.map((addon, index) => (
              <ScrollReveal key={index} delay={index * 50}>
                <div className="flex items-center p-3">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-astrem-gray-700">{addon}</span>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={200}>
            <div className="mt-12 text-center">
              <p className="text-lg text-astrem-gray-600 font-medium mb-4">
                Note: We can create an entirely custom tour. Contact for quote.
              </p>
              <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full px-6">
                <a href="mailto:hello@astrem.co?subject=Virtual Tour Inquiry for">
                  Request Custom Features
                </a>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-16 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-white text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Payment Options
              </span>
              <h2 className="text-2xl md:text-3xl font-medium mb-4">Accepted Payment Methods</h2>
            </div>
          </ScrollReveal>

          <div className="max-w-3xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {paymentMethods.map((method, index) => (
                <ScrollReveal key={index} delay={index * 100}>
                  <div className="bg-white p-4 rounded-lg shadow-sm text-center h-full flex flex-col items-center justify-center">
                    <CreditCard className="h-8 w-8 text-astrem-black mb-2" />
                    <p className="text-astrem-gray-700 font-medium">{method}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <PricingSection
        title="Virtual Tour Packages"
        description="Select the perfect virtual tour package for your needs in Baton Rouge, Louisiana, or contact us for a custom solution tailored to your specific requirements."
        pricingCards={pricingPlans}
      />

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Real Estate Solutions
              </span>
              <h2 className="text-3xl md:text-4xl font-medium mb-4">Zillow ShowingTime+ Virtual Tours</h2>
              <p className="text-lg text-astrem-gray-600 max-w-3xl mx-auto">
                Specialized virtual tour creation for real estate professionals using the Zillow ShowingTime+ platform in Baton Rouge, Louisiana. Enhance your listings with professional quality tours.
              </p>
            </div>
          </ScrollReveal>

          <div className="max-w-4xl mx-auto">
            <ScrollReveal delay={100}>
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="bg-astrem-gray-800 text-white py-4 px-6">
                  <h3 className="text-xl font-medium">Zillow ShowingTime+ Pricing</h3>
                </div>
                <div className="p-6">
                  <div className="text-center">
                    <div className="bg-astrem-gray-50 rounded-lg p-8">
                      <h4 className="font-medium text-xl mb-4">Zillow ShowingTime+ Virtual Tours</h4>
                      <p className="text-2xl font-bold text-astrem-black">Contact for quote</p>
                      <p className="text-astrem-gray-600 mt-2">Custom pricing based on property size and requirements</p>
                    </div>
                  </div>
                  
                  <div className="mt-8 text-center">
                    <p className="text-astrem-gray-600 mb-6">
                      Our Zillow ShowingTime+ virtual tours are professionally created and optimized for the Zillow platform, helping your listings stand out and attract more potential buyers.
                    </p>
                    <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full px-6">
                      <a href="mailto:hello@astrem.co?subject=Zillow ShowingTime+ Tour Inquiry: ">
                        Book a Zillow Tour
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-16">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Benefits
              </span>
              <h2 className="text-3xl md:text-4xl font-medium mb-4">Why Choose Our Virtual Tours</h2>
              <p className="text-lg text-astrem-gray-600 max-w-3xl mx-auto">
                Our custom approach delivers superior results compared to cookie-cutter solutions available in Baton Rouge, Louisiana.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ScrollReveal>
              <Card className="h-full border border-astrem-gray-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-medium mb-4">For Businesses</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="mr-2 text-green-500 mt-1">✓</div>
                      <p>Showcase spaces to potential customers 24/7, regardless of location or time zone</p>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-2 text-green-500 mt-1">✓</div>
                      <p>Increase website engagement metrics and time spent on your site</p>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-2 text-green-500 mt-1">✓</div>
                      <p>Reduce unnecessary in-person visits by pre-qualifying interested parties</p>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-2 text-green-500 mt-1">✓</div>
                      <p>Differentiate from competitors with cutting-edge interactive content</p>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-2 text-green-500 mt-1">✓</div>
                      <p>Document spaces for insurance, planning, or historical purposes</p>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <Card className="h-full border border-astrem-gray-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-medium mb-4">For Visitors</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="mr-2 text-green-500 mt-1">✓</div>
                      <p>Explore spaces at their own pace and convenience from any device</p>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-2 text-green-500 mt-1">✓</div>
                      <p>Gain a true sense of spatial relationships that photos can't convey</p>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-2 text-green-500 mt-1">✓</div>
                      <p>Access additional information through interactive hotspots and media</p>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-2 text-green-500 mt-1">✓</div>
                      <p>Share virtual tours easily with others involved in decision-making</p>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-2 text-green-500 mt-1">✓</div>
                      <p>Experience spaces that may be geographically distant or not yet constructed</p>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <FAQSection
        title="Frequently Asked Questions"
        description="Learn more about our virtual tour services and how they can benefit your business in Baton Rouge, Louisiana."
        faqs={faqs}
      />

      <section className="py-20 bg-astrem-black text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-medium mb-6">Ready to Create Your Virtual Tour in Baton Rouge?</h2>
              <p className="text-lg text-white/80 mb-8">
                Contact us today to discuss your virtual tour needs and schedule a consultation. We'll help you create an immersive, interactive experience that showcases your space perfectly.
              </p>
              <Button asChild size="lg" className="bg-white text-astrem-black hover:bg-astrem-gray-100 rounded-full px-8">
                <a href="mailto:hello@astrem.co?subject=Virtual Tour Inquiry for">
                  Get Started
                </a>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PageLayout>
  );
};

export default VirtualTours;
